import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import {
    FiDollarSign, FiCheckCircle, FiAlertCircle, FiFileText,
    FiTrendingUp, FiRefreshCw, FiPlusCircle, FiList,
    FiBarChart2, FiCalendar, FiAward, FiInfo,
    FiUsers, FiArrowRight, FiFilter, FiX, FiShield,
    FiTruck, FiHome, FiSliders, FiPieChart, FiBookOpen
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import feeService from "../../services/feeService";
import courseService from "../../services/courseService";
import { getYearOptions } from "../../utils/courseYears";

// ─── Formatters ───────────────────────────────────────────────────────────────

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;
const inrShort = (v) => {
    const n = Number(v || 0);
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
    return `₹${n}`;
};

const barColor = (monthNum) => {
    const cur = new Date().getMonth() + 1;
    return monthNum === cur ? "#003b67" : "#78b3de";
};

// ─── Shimmer skeleton ─────────────────────────────────────────────────────────

const shimmerStyle = `
@keyframes shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
}
@keyframes fee-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}
`;

function SkeletonBox({ width = "100%", height = 14, radius = 6, style = {} }) {
    return (
        <div style={{
            width, height, borderRadius: radius,
            background: "linear-gradient(90deg,var(--border,#e2e8f0) 25%,var(--bg-hover,#f8fafc) 50%,var(--border,#e2e8f0) 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.4s ease-in-out infinite",
            ...style,
        }} />
    );
}

function StatCardSkeleton() {
    return (
        <div className="card" style={{ padding: "1.25rem", display: "grid", gap: "0.6rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <SkeletonBox width={32} height={32} radius={9} />
                <SkeletonBox width={48} height={11} radius={4} />
            </div>
            <SkeletonBox width="75%" height={24} radius={5} />
            <SkeletonBox width="55%" height={12} radius={4} />
        </div>
    );
}

// ─── Custom chart tooltip ──────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div style={{
            background: "#fff", border: "1px solid #e2e8f0",
            borderRadius: 8, padding: "10px 14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: 13,
        }}>
            <div style={{ fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FiCalendar size={12} color="#6b7280" /> {label}
            </div>
            <div style={{ color: "#16a34a" }}>
                Tuition Collected: <strong>{inr(d.totalCollected)}</strong>
            </div>
            <div style={{ color: "var(--text-muted)", marginTop: 2 }}>
                {d.receiptCount} receipt{d.receiptCount !== 1 ? "s" : ""}
            </div>
        </div>
    );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, color, sub, to, icon: Icon, trend }) {
    const inner = (
        <div
            className="card"
            style={{
                padding: "1.25rem",
                borderLeft: `4px solid ${color}`,
                cursor: to ? "pointer" : "default",
                transition: "box-shadow 0.15s, transform 0.15s",
                position: "relative",
                overflow: "hidden",
            }}
            onMouseEnter={(e) => {
                if (!to) return;
                e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.11)";
                e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "var(--shadow)";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            {/* Faint bg icon */}
            <div style={{ position: "absolute", top: 8, right: 10, opacity: 0.06, pointerEvents: "none" }}>
                <Icon size={52} color={color} />
            </div>

            {/* Icon badge + optional link arrow */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.6rem" }}>
                <div style={{
                    width: 34, height: 34, borderRadius: 9,
                    background: `${color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Icon size={16} color={color} />
                </div>
                {to && <FiArrowRight size={13} color={color} style={{ opacity: 0.5, marginTop: 3 }} />}
            </div>

            <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 5 }}>{label}</div>

            {sub && (
                <div style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    fontSize: 11.5, color, marginTop: 4,
                    background: `${color}10`, borderRadius: 20, padding: "2px 8px",
                }}>
                    <FiUsers size={10} /> {sub}
                </div>
            )}

            {trend != null && (
                <div style={{ fontSize: 11.5, color: trend >= 0 ? "#16a34a" : "#dc2626", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                    <FiTrendingUp size={11} />
                    {trend >= 0 ? `+${trend}%` : `${trend}%`} vs last month
                </div>
            )}
        </div>
    );
    return to ? <Link to={to} style={{ textDecoration: "none" }}>{inner}</Link> : inner;
}

// ─── Quick Action Button ──────────────────────────────────────────────────────

function QBtn({ to, icon: Icon, label, variant = "outline", color }) {
    return (
        <Link
            to={to}
            className={`btn btn-${variant}`}
            style={{
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
                ...(color ? { borderColor: color, color } : {}),
            }}
        >
            <Icon size={14} /> {label}
        </Link>
    );
}

// ─── FeeDashboard ─────────────────────────────────────────────────────────────

const PERIOD_TABS = [
    { key: "all", label: "🌟 Full Session" },
    { key: "today", label: "📅 Today (Daily)" },
    { key: "week", label: "🗓 This Week" },
    { key: "month", label: "📆 This Month" },
    { key: "custom", label: "⚙️ Custom Range" },
];

export default function FeeDashboard() {
    const { academicYear, role, selectedCollegeId, collegeData, collegeName } = useApp();
    const feeBasePath = role === "clerk" ? "/clerk/fees" : role === "accountant" ? "/accountant/fees" : "/fees";
    const finesPath = role === "clerk" ? "/clerk/fines" : role === "accountant" ? "/accountant/fines" : "/fines";
    const transportPath = role === "clerk" ? "/clerk/transport" : role === "accountant" ? "/accountant/transport" : "/transport";

    const [courses, setCourses] = useState([]);
    const [filterCourse, setFilterCourse] = useState("");
    const [filterCourseYear, setFilterCourseYear] = useState("");
    const [filterPaymentMode, setFilterPaymentMode] = useState("All");

    // Period / Date Range filters
    const [period, setPeriod] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [summary, setSummary] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [loadingSum, setLoadingSum] = useState(false);
    const [loadingChart, setLoadingChart] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Fetch active courses for dropdown
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const params = { isActive: true };
                if (selectedCollegeId && selectedCollegeId !== "all") {
                    params.college = selectedCollegeId;
                }
                const res = await courseService.getAll(params);
                const courseList = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
                setCourses(courseList);
            } catch (err) {
                console.error("Could not load courses:", err);
            }
        };
        fetchCourses();
    }, [academicYear, selectedCollegeId]);

    // Year options based on selected course (or all courses)
    const yearOptions = useMemo(() => {
        const selected = courses.find((c) => c.shortName === filterCourse);
        if (selected) return getYearOptions(selected).map((y) => y.label);
        return [...new Set(courses.flatMap((c) => getYearOptions(c).map((y) => y.label)))];
    }, [courses, filterCourse]);

    const handleCourseChange = (e) => {
        setFilterCourse(e.target.value);
        setFilterCourseYear("");
    };

    const handleResetFilters = () => {
        setFilterCourse("");
        setFilterCourseYear("");
        setFilterPaymentMode("All");
        setPeriod("all");
        setStartDate("");
        setEndDate("");
    };

    const fetchData = useCallback(() => {
        if (!academicYear?._id) return;

        // Strictly fetch Academic Tuition fees with optional period / date filter
        const params = {
            feeCategory: "Academic",
        };
        if (selectedCollegeId && selectedCollegeId !== "all") {
            params.college = selectedCollegeId;
        }
        if (filterCourse) params.course = filterCourse;
        if (filterCourseYear) params.courseYear = filterCourseYear;
        if (filterPaymentMode && filterPaymentMode !== "All") params.paymentMode = filterPaymentMode;
        if (period && period !== "all") params.period = period;
        if (period === "custom") {
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
        }

        setLoadingSum(true);
        feeService.getYearSummary(academicYear._id, params)
            .then((r) => setSummary(r.data || r))
            .catch((e) => toast.error(e.message))
            .finally(() => setLoadingSum(false));

        setLoadingChart(true);
        feeService.getYearMonthlyBreakdown(academicYear._id, params)
            .then((r) => setChartData(r.data || r))
            .catch((e) => toast.error(e.message))
            .finally(() => setLoadingChart(false));
    }, [academicYear, selectedCollegeId, filterCourse, filterCourseYear, filterPaymentMode, period, startDate, endDate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchData();
        setTimeout(() => setRefreshing(false), 700);
    };

    const peakMonth = chartData.length
        ? chartData.reduce((best, m) => m.totalCollected > best.totalCollected ? m : best, chartData[0])
        : null;

    const hasActiveFilters = Boolean(
        filterCourse || filterCourseYear || filterPaymentMode !== "All" || period !== "all" || startDate || endDate
    );

    // Build URL query params for deep-linking with current filters
    const filterQueryParams = new URLSearchParams();
    if (filterCourse) filterQueryParams.set("courseAdmitted", filterCourse);
    if (filterCourseYear) filterQueryParams.set("courseYear", filterCourseYear);
    if (filterPaymentMode && filterPaymentMode !== "All") filterQueryParams.set("paymentMode", filterPaymentMode);
    if (period && period !== "all") filterQueryParams.set("period", period);
    const filterQueryStr = filterQueryParams.toString() ? `?${filterQueryParams.toString()}` : "";

    const collectionPercentage = summary && summary.totalDecided > 0
        ? Math.min(100, Math.round((summary.totalCollected / summary.totalDecided) * 100))
        : 0;

    return (
        <div>
            <style>{shimmerStyle}</style>

            {/* ── Page Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                        <h1 className="page-title" style={{ margin: 0 }}>Tuition &amp; Academic Fees Dashboard 🎓</h1>
                        <span style={{
                            fontSize: 12,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: !selectedCollegeId || selectedCollegeId === "all" ? "#f1f5f9" : "#e0f2fe",
                            color: !selectedCollegeId || selectedCollegeId === "all" ? "#475569" : "#0369a1",
                            border: "1px solid " + (!selectedCollegeId || selectedCollegeId === "all" ? "#cbd5e1" : "#bae6fd"),
                        }}>
                            {!selectedCollegeId || selectedCollegeId === "all" ? "🌐 All Campuses" : `🏢 ${collegeData?.name || collegeName}`}
                        </span>
                    </div>
                    <p className="page-subtitle" style={{ marginTop: 4 }}>
                        {academicYear ? academicYear.label : "Select an academic year"} · Tracking student tuition fees decided, collections, installments &amp; pending balances.
                        {hasActiveFilters && (
                            <span style={{ color: "#003b67", fontWeight: 600, marginLeft: 6 }}>
                                · Filtered ({[
                                    filterCourse,
                                    filterCourseYear,
                                    filterPaymentMode !== "All" ? `Mode: ${filterPaymentMode}` : "",
                                ].filter(Boolean).join(" · ")})
                            </span>
                        )}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    <QBtn to={`${feeBasePath}/new`} icon={FiPlusCircle} label="+ Add Tuition Receipt" variant="primary" />
                    <QBtn to={`${feeBasePath}/receipts${filterQueryStr}`} icon={FiFileText} label="Fee Receipts" variant="outline" />
                    <QBtn to={`${feeBasePath}/pending${filterQueryStr}`} icon={FiAlertCircle} label="Pending Tuition" variant="outline" />
                    <QBtn to="/settings/fee-structures" icon={FiSliders} label="Fee Structures" variant="outline" />
                    {academicYear && (
                        <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={handleRefresh}
                            disabled={loadingSum || loadingChart}
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                            title="Refresh"
                        >
                            <FiRefreshCw
                                size={13}
                                style={{ animation: refreshing ? "fee-spin 0.7s linear" : "none" }}
                            />
                            Refresh
                        </button>
                    )}
                </div>
            </div>

            {/* ── No Year Warning ── */}
            {!academicYear ? (
                <div style={{
                    background: "#fef3c7", border: "1px solid #fcd34d",
                    borderRadius: 8, padding: "0.75rem 1rem",
                    fontSize: 13, color: "#92400e",
                    display: "flex", alignItems: "center", gap: "0.5rem",
                }}>
                    <FiInfo size={15} color="#d97706" />
                    Select an academic year from the header to view fee data.
                </div>
            ) : (
                <>
                    {/* ── Filter Toolbar ── */}
                    <div className="card" style={{ padding: "0.85rem 1.15rem", marginBottom: "1.25rem" }}>
                        {/* Top: Period / Date Range Selector Ribbon */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                                <span style={{ fontSize: "12.5px", fontWeight: 700, color: "var(--text-muted)", marginRight: "4px" }}>
                                    Report Period:
                                </span>
                                {PERIOD_TABS.map((t) => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        onClick={() => setPeriod(t.key)}
                                        style={{
                                            padding: "4px 10px",
                                            borderRadius: "6px",
                                            border: period === t.key ? "1px solid #003b67" : "1px solid #e2e8f0",
                                            background: period === t.key ? "#003b67" : "#f8fafc",
                                            color: period === t.key ? "#fff" : "#475569",
                                            fontWeight: period === t.key ? 700 : 500,
                                            fontSize: "12px",
                                            cursor: "pointer",
                                            transition: "all 0.15s ease",
                                        }}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>

                            {/* Custom Date Range Pickers */}
                            {period === "custom" && (
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                                        <span style={{ fontSize: "11.5px", fontWeight: 600, color: "var(--text-muted)" }}>From:</span>
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            style={{
                                                padding: "3px 7px", fontSize: "12px", borderRadius: "5px",
                                                border: "1px solid var(--border)", background: "#fff"
                                            }}
                                        />
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                                        <span style={{ fontSize: "11.5px", fontWeight: 600, color: "var(--text-muted)" }}>To:</span>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            style={{
                                                padding: "3px 7px", fontSize: "12px", borderRadius: "5px",
                                                border: "1px solid var(--border)", background: "#fff"
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Bottom: Course, Year, Payment Mode Dropdowns */}
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", paddingTop: "0.75rem" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "var(--text-muted)", fontSize: 13, fontWeight: 700 }}>
                                <FiFilter size={14} color="#003b67" />
                                <span>Filter:</span>
                            </div>

                            {/* Course Filter */}
                            <select
                                value={filterCourse}
                                onChange={handleCourseChange}
                                style={{
                                    padding: "0.4rem 0.8rem", borderRadius: 6,
                                    border: "1px solid var(--border)", fontSize: "12.5px",
                                    background: "#fff", fontWeight: 500, minWidth: 140,
                                }}
                            >
                                <option value="">All Courses</option>
                                {courses.map((c) => (
                                    <option key={c._id} value={c.shortName}>{c.shortName}</option>
                                ))}
                            </select>

                            {/* Year Filter */}
                            <select
                                value={filterCourseYear}
                                onChange={(e) => setFilterCourseYear(e.target.value)}
                                style={{
                                    padding: "0.4rem 0.8rem", borderRadius: 6,
                                    border: "1px solid var(--border)", fontSize: "12.5px",
                                    background: "#fff", fontWeight: 500, minWidth: 120,
                                }}
                            >
                                <option value="">All Years</option>
                                {yearOptions.map((yr) => (
                                    <option key={yr} value={yr}>{yr}</option>
                                ))}
                            </select>

                            {/* Payment Mode */}
                            <select
                                value={filterPaymentMode}
                                onChange={(e) => setFilterPaymentMode(e.target.value)}
                                style={{
                                    padding: "0.4rem 0.8rem", borderRadius: 6,
                                    border: "1px solid var(--border)", fontSize: "12.5px",
                                    background: "#fff", fontWeight: 500, minWidth: 120,
                                }}
                            >
                                <option value="All">All Modes</option>
                                <option value="Cash">Cash</option>
                                <option value="UPI">UPI</option>
                                <option value="Cheque">Cheque</option>
                                <option value="NEFT">NEFT</option>
                                <option value="DD">DD</option>
                            </select>

                            {/* Reset Button */}
                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    className="btn btn-outline btn-sm"
                                    style={{
                                        display: "inline-flex", alignItems: "center",
                                        gap: "0.3rem", fontSize: "11.5px", padding: "0.35rem 0.7rem",
                                        color: "#dc2626", borderColor: "#fecaca"
                                    }}
                                >
                                    <FiX size={12} /> Reset Filters
                                </button>
                            )}

                            {hasActiveFilters && summary && (
                                <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)" }}>
                                    Filtered Receipts: <strong>{summary.receiptCount}</strong>
                                </span>
                            )}
                        </div>
                    </div>

                    {/* ── Main Stat KPI Cards (Tuition Focused) ── */}
                    {loadingSum ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                            <StatCardSkeleton /> <StatCardSkeleton />
                            <StatCardSkeleton /> <StatCardSkeleton />
                        </div>
                    ) : summary && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                            <StatCard
                                label="Total Tuition Decided"
                                value={inr(summary.totalDecided)}
                                color="#003b67"
                                icon={FiDollarSign}
                                sub={`${summary.studentCount} active students`}
                            />
                            <StatCard
                                label="Total Tuition Collected"
                                value={inr(summary.totalCollected)}
                                color="#16a34a"
                                icon={FiCheckCircle}
                                to={`${feeBasePath}/receipts${filterQueryStr}`}
                                sub={
                                    period === "today"
                                        ? "Today's Collections"
                                        : period === "week"
                                            ? "This Week's Collections"
                                            : period === "month"
                                                ? "This Month's Collections"
                                                : period === "custom"
                                                    ? (startDate && endDate ? `${startDate} to ${endDate}` : "Custom Date Range")
                                                    : `${summary.receiptCount} receipts issued`
                                }
                            />
                            <StatCard
                                label="Pending Tuition Balance"
                                value={inr(summary.totalPending)}
                                color="#dc2626"
                                icon={FiAlertCircle}
                                to={`${feeBasePath}/pending${filterQueryStr}`}
                                sub={summary.totalPending > 0 ? "Pending recovery" : "All cleared ✓"}
                            />
                            <StatCard
                                label="Collection Rate"
                                value={`${collectionPercentage}%`}
                                color="#0891b2"
                                icon={FiPieChart}
                                sub={`Target: 100%`}
                            />
                            <StatCard
                                label="Tuition Receipts"
                                value={summary.receiptCount || 0}
                                color="#7c3aed"
                                icon={FiFileText}
                                to={`${feeBasePath}/receipts${filterQueryStr}`}
                                sub={period !== "all" ? `In ${period}` : "Total receipts"}
                            />
                        </div>
                    )}

                    {/* ── Monthly Bar Chart ── */}
                    <div className="card" style={{ marginBottom: "1.25rem" }}>
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                                <FiBarChart2 size={15} color="#003b67" />
                                Monthly Tuition Collections — {academicYear.label}
                                {hasActiveFilters && ` (${[filterCourse, filterCourseYear].filter(Boolean).join(" · ")})`}
                            </span>
                            {peakMonth && peakMonth.totalCollected > 0 && (
                                <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 400, display: "flex", alignItems: "center", gap: 4 }}>
                                    <FiAward size={12} color="#003b67" />
                                    Peak: <strong style={{ color: "#003b67" }}>{peakMonth.monthName}</strong>
                                    &nbsp;({inr(peakMonth.totalCollected)})
                                </span>
                            )}
                        </div>

                        <div className="card-body">
                            {loadingChart ? (
                                <div style={{ display: "grid", gap: "0.75rem", paddingTop: "0.5rem" }}>
                                    <SkeletonBox width="100%" height={200} radius={8} />
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem" }}>
                                        {[...Array(4)].map((_, i) => <SkeletonBox key={i} height={60} radius={6} />)}
                                    </div>
                                </div>
                            ) : chartData.length === 0 ? (
                                <div style={{
                                    textAlign: "center", padding: "2rem",
                                    color: "var(--text-muted)", fontSize: 13,
                                    display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem",
                                }}>
                                    <FiBarChart2 size={28} style={{ opacity: 0.3 }} />
                                    No tuition collection data found for this selection.
                                </div>
                            ) : (
                                <>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }} barSize={32}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="monthName"
                                                tick={{ fontSize: 12, fill: "#6b7280" }}
                                                axisLine={false} tickLine={false}
                                            />
                                            <YAxis
                                                tickFormatter={inrShort}
                                                tick={{ fontSize: 11, fill: "#9ca3af" }}
                                                axisLine={false} tickLine={false} width={60}
                                            />
                                            <Tooltip content={<ChartTooltip />} cursor={{ fill: "#f1f5f9" }} />
                                            <Bar dataKey="totalCollected" radius={[4, 4, 0, 0]}>
                                                {chartData.map((entry) => (
                                                    <Cell key={entry.month} fill={barColor(entry.month)} />
                                                ))}
                                                <LabelList
                                                    dataKey="totalCollected"
                                                    position="top"
                                                    formatter={(v) => v > 0 ? inrShort(v) : ""}
                                                    style={{ fontSize: 10, fill: "#6b7280" }}
                                                />
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>

                                    {/* Month-wise tiles */}
                                    <div style={{ marginTop: "1.25rem", borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
                                        <div style={{
                                            fontSize: 12, fontWeight: 600, color: "var(--text-muted)",
                                            textTransform: "uppercase", letterSpacing: "0.05em",
                                            marginBottom: "0.75rem",
                                            display: "flex", alignItems: "center", gap: 5,
                                        }}>
                                            <FiCalendar size={11} /> Month-wise Totals
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "0.5rem" }}>
                                            {chartData.filter((m) => m.totalCollected > 0).map((m) => {
                                                const isCurrent = m.month === (new Date().getMonth() + 1);
                                                const isPeak = peakMonth && m.month === peakMonth.month && peakMonth.totalCollected > 0;
                                                return (
                                                    <div
                                                        key={m.month}
                                                        style={{
                                                            background: isCurrent ? "#e8f1f8" : "var(--bg)",
                                                            border: `1px solid ${isCurrent ? "#b6d5ec" : "var(--border)"}`,
                                                            borderRadius: 6, padding: "0.5rem 0.75rem",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {isPeak && (
                                                            <span style={{
                                                                position: "absolute", top: -7, right: 6,
                                                                fontSize: 10, fontWeight: 700,
                                                                background: "#003b67", color: "#fff",
                                                                borderRadius: 20, padding: "1px 6px",
                                                                display: "flex", alignItems: "center", gap: 2,
                                                            }}>
                                                                <FiAward size={9} /> Peak
                                                            </span>
                                                        )}
                                                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", display: "flex", alignItems: "center", gap: 4 }}>
                                                            {isCurrent && <FiCalendar size={10} color="#003b67" />}
                                                            {m.monthName}
                                                        </div>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a", marginTop: 2 }}>
                                                            {inr(m.totalCollected)}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 3 }}>
                                                            <FiFileText size={9} />
                                                            {m.receiptCount} receipt{m.receiptCount !== 1 ? "s" : ""}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {chartData.every((m) => m.totalCollected === 0) && (
                                                <div style={{ gridColumn: "1 / -1", fontSize: 13, color: "var(--text-muted)", textAlign: "center", padding: "0.5rem" }}>
                                                    No tuition payments recorded yet matching filters.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ── Separate Non-Tuition Hubs Banner ── */}
                    <div className="card" style={{ padding: "1.25rem", marginBottom: "1.25rem", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiBookOpen color="#003b67" /> Other Financial &amp; Ancillary Revenue Management Hubs
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "0.75rem" }}>
                            <Link to={finesPath} style={{ textDecoration: "none" }}>
                                <div style={{
                                    background: "#fff", border: "1px solid #fed7aa", borderRadius: 8, padding: "0.85rem 1rem",
                                    display: "flex", alignItems: "center", gap: "0.75rem", transition: "transform 0.15s ease",
                                }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                                >
                                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "#fff7ed", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <FiShield size={18} color="#ea580c" />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: "13.5px", color: "#c2410c" }}>👔 ⚖ Charges &amp; Fines Hub</div>
                                        <div style={{ fontSize: "12px", color: "#64748b" }}>Uniform, Lab Manuals, Books &amp; Penalties</div>
                                    </div>
                                </div>
                            </Link>

                            <Link to={transportPath} style={{ textDecoration: "none" }}>
                                <div style={{
                                    background: "#fff", border: "1px solid #bbf7d0", borderRadius: 8, padding: "0.85rem 1rem",
                                    display: "flex", alignItems: "center", gap: "0.75rem", transition: "transform 0.15s ease",
                                }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                                >
                                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <FiTruck size={18} color="#16a34a" />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: "13.5px", color: "#15803d" }}>🚌 Bus / Transport Management</div>
                                        <div style={{ fontSize: "12px", color: "#64748b" }}>Routes, Distance Slabs &amp; Bus Passes</div>
                                    </div>
                                </div>
                            </Link>

                            <Link to="/hostel-fee-receipts" style={{ textDecoration: "none" }}>
                                <div style={{
                                    background: "#fff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "0.85rem 1rem",
                                    display: "flex", alignItems: "center", gap: "0.75rem", transition: "transform 0.15s ease",
                                }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                                >
                                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <FiHome size={18} color="#7c3aed" />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: "13.5px", color: "#6d28d9" }}>🏠 Hostel Fee Receipts</div>
                                        <div style={{ fontSize: "12px", color: "#64748b" }}>Hostel Room Rents &amp; Allocations</div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* ── Quick Tuition Operations ── */}
                    <div className="card">
                        <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiList size={14} /> Tuition Fee Operations
                        </div>
                        <div className="card-body" style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                            <QBtn to={`${feeBasePath}/new`} icon={FiPlusCircle} label="+ Add Tuition Receipt" variant="primary" />
                            <QBtn to={`${feeBasePath}/receipts${filterQueryStr}`} icon={FiFileText} label="All Fee Receipts" variant="outline" />
                            <QBtn to={`${feeBasePath}/pending${filterQueryStr}`} icon={FiAlertCircle} label="Pending Tuition Dues" variant="outline" color="#dc2626" />
                            <QBtn to="/settings/fee-structures" icon={FiSliders} label="Fee Structure Configuration" variant="outline" />
                            <QBtn to={`${feeBasePath}/monthly`} icon={FiBarChart2} label="Monthly Tuition Report" variant="outline" />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}